import Decimal from '@st/decimal'
import { isTruthy } from '@st/utils'
import { useSettingsStore } from '@st/core/stores/useSettingsStore'
import type {
  SystemSettingsDepositChainBonuses,
  DepositBonusesPreparedChain,
} from '../../types'
import { useBonusesDeps } from '../../useDeps'

interface UseDepositBonusesChainReturn {
  preparedBonusesChain: ComputedRef<DepositBonusesPreparedChain | null>
}

type SystemSettingsDepositChainLevel =
  SystemSettingsDepositChainBonuses['chainLevels'][0]

export function useDefaultBonusesChain(): UseDepositBonusesChainReturn {
  const { format: formatCrypto } = useCryptoFormatter()
  const { format: formatMultiplier } = useNumberFormatter()
  const { getCurrencyIcon } = useBonusesDeps()
  const { t } = useI18n()

  const { settings } = storeToRefs(useSettingsStore())
  const bonusesChain = computed(
    () => settings.value?.frontBonusForDepositInfoForUnauthorized,
  )

  function createBonusesAwardOutput({
    depositMultiplier,
    freespinsCount,
    freebetAmount,
    freebetCurrencyCode,
    reloadAmount,
    reloadCurrencyCode,
  }: SystemSettingsDepositChainLevel) {
    const cashAward = depositMultiplier
      ? {
          icon: null,
          text: '',
          amount: `${formatMultiplier(
            new Decimal(depositMultiplier ?? 0).mul(100).toString(),
          )}%`,
        }
      : null

    const freespinAward = freespinsCount
      ? {
          icon: null,
          text: '',
          amount: `${freespinsCount} FS`,
        }
      : null
    const freebetAward = freebetAmount
      ? {
          icon: getCurrencyIcon(freebetCurrencyCode ?? '') ?? null,
          amount: formatCrypto(freebetAmount),
          text: t('bonuses.freebet'),
        }
      : null

    const reloadAward = reloadAmount
      ? {
          icon: getCurrencyIcon(reloadCurrencyCode ?? '') ?? null,
          amount: formatCrypto(reloadAmount),
          text: t('bonuses.reload'),
        }
      : null
    const filteredMultiBonuses = [
      cashAward,
      freespinAward,
      freebetAward,
      reloadAward,
    ].filter(isTruthy)

    return {
      main: filteredMultiBonuses[0],
      others:
        filteredMultiBonuses.length > 1 ? filteredMultiBonuses.slice(1) : [],
    }
  }
  const { getTitle } = useGetTitleByLevel()

  const preparedBonusesChain = computed(() =>
    bonusesChain.value?.isEnabled
      ? {
          totalDepositBonusPercent: `${formatMultiplier(
            new Decimal(bonusesChain.value.totalDepositBonusPercent)
              .mul(100)
              .toString(),
          )}%`,
          totalDepositBonusAmountInBonusCurrency: formatCrypto(
            bonusesChain.value.totalDepositBonusAmountInBonusCurrency ?? 0,
          ),
          bonusCurrencyIcon: getCurrencyIcon(
            bonusesChain.value.depositBonusCurrencyCode ?? '',
          ),
          chainLevels: bonusesChain.value.chainLevels.map((item) => ({
            id: item.level,
            level: item.level,
            title: getTitle(t('bonuses.bonus'), item.level),
            isStepBonus: item.isGroup,
            bonusesOutput: createBonusesAwardOutput(item),
            status: 'inProgress' as const,
          })),
        }
      : null,
  )
  return {
    preparedBonusesChain,
  }
}
